import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, ChevronUp, ChevronDown, X, Loader2 } from 'lucide-react';

interface PDFSearchPanelProps {
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
  onSearch: (term: string) => void;
  searchResults: any[];
  currentResultIndex: number;
  onNextResult: () => void;
  onPreviousResult: () => void;
  onClearSearch: () => void;
  isSearching: boolean;
  className?: string;
}

const PDFSearchPanel: React.FC<PDFSearchPanelProps> = ({
  searchTerm,
  onSearchTermChange,
  onSearch,
  searchResults,
  currentResultIndex,
  onNextResult,
  onPreviousResult,
  onClearSearch,
  isSearching,
  className = ""
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      onSearch(searchTerm);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearchSubmit(e);
    } else if (e.key === 'Escape') {
      onClearSearch();
      setIsExpanded(false);
    }
  };

  return (
    <Card className={`transition-all duration-200 ${className}`}>
      <CardContent className="p-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search in PDF..."
              value={searchTerm}
              onChange={(e) => onSearchTermChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsExpanded(true)}
              className="pl-8 pr-8"
            />
            {searchTerm && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearSearch}
                className="absolute right-1 top-1 h-6 w-6 p-0"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>

          {isSearching && (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          )}

          {searchResults.length > 0 && (
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs">
                {currentResultIndex + 1} / {searchResults.length}
              </Badge>
              <div className="flex">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onPreviousResult}
                  disabled={searchResults.length === 0}
                  className="h-7 w-7 p-0 rounded-r-none border-r-0"
                >
                  <ChevronUp className="h-3 w-3" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onNextResult}
                  disabled={searchResults.length === 0}
                  className="h-7 w-7 p-0 rounded-l-none"
                >
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {isExpanded && searchResults.length > 0 && (
          <div className="mt-3 space-y-2 max-h-32 overflow-y-auto">
            {searchResults.slice(0, 5).map((result, index) => (
              <div
                key={index}
                className={`p-2 rounded-md text-xs cursor-pointer transition-colors ${
                  index === currentResultIndex
                    ? 'bg-primary/10 border border-primary/20'
                    : 'bg-muted/50 hover:bg-muted'
                }`}
                onClick={() => onNextResult()}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-xs">Page {result.pageNumber}</span>
                  <Badge variant="outline" className="text-xs h-4">
                    Match {index + 1}
                  </Badge>
                </div>
                <div className="text-muted-foreground truncate">
                  {result.context}
                </div>
              </div>
            ))}
            {searchResults.length > 5 && (
              <div className="text-center text-xs text-muted-foreground py-1">
                ... and {searchResults.length - 5} more results
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PDFSearchPanel;